import { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentService } from '../../services/paymentService';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { TransactionError } from '../TransactionError';
import { WebPaymentTrustLinks } from '../WebPaymentTrustLinks';
import { CalendarPickerButton } from '../booking/CalendarPickerButton';
import { WebpaymentLoading } from '../shared/WebpaymentLoading';
import { getWebBookingGuestEmail } from '../../utils/webPaymentGuest';
import '../PaymentPage.css';

/** Maps backend summary.type → i18n primary-button key */
function primaryBtnKeyForType(type: string): string {
  const t = type.toLowerCase();
  if (t === 'gym_subscription' || t === 'gym_plan') return 'webpayment.confirm.btn_my_subscription';
  if (t === 'gym_bundle' || t === 'gym_bono' || t === 'gym_session_bundle') return 'webpayment.confirm.btn_my_bundles';
  return 'webpayment.confirm.btn_my_reservations';
}

/** Types that carry a bookingPreview with a concrete time slot */
function isReservationLikeType(type: string): boolean {
  const t = type.toLowerCase();
  return t === 'booking' || t === 'booking_cart' || t === 'gym_private_session' || t === 'gym_clase_particular';
}

const POLLING_INTERVAL_MS = 2000;
const PENDING_TIMEOUT_MS = 15000;
const SUCCESS_AUTO_ACTION_SECONDS = 3;

function defaultBookingsUrl(tenantId: string): string {
  if (typeof window === 'undefined') return '/bookings';
  const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
  return `${window.location.origin}/bookings${q}`;
}

function moduleHomeUrl(basePath: string, tenantId: string): string {
  if (typeof window === 'undefined') return basePath;
  const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
  return `${window.location.origin}${basePath}${q}`;
}

export type ResultStatus =
  | 'loading'
  | 'not_found'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'canceled'
  | 'requires_capture'
  | 'expired'
  | 'pending'
  | 'processing';

export type PaymentIntentSummary = {
  status: string;
  type: string;
  referenceId: string;
  detailsUrl?: string;
  channel?: string;
  nextAction?: { type: 'close' | 'redirect' | 'none'; timeoutMs?: number; targetUrl?: string | null };
  i18nKeys: { titleKey: string; descriptionKey: string };
  moduleBasePath?: '/booking' | '/restaurant';
  bookingPreview?: {
    bookingId?: string;
    serviceName?: string;
    slotStart?: string;
    slotEnd?: string;
    customerName?: string;
    customerEmail?: string;
    status?: string;
  };
};

export type PaymentIntentResultPanelProps = {
  paymentIntentId: string;
  tenantId: string;
  statusFromQuery?: string | null;
  /** standalone = página /pay/.../result; embedded = dentro del módulo */
  variant: 'standalone' | 'embedded';
  /** Ruta del módulo para volver al inicio del flujo (p. ej. /booking o /restaurant) */
  moduleBasePath: '/booking' | '/restaurant' | '/foodtruck' | '/gym';
  /** Web: URL del CTA principal tras éxito (por defecto Mis reservas) */
  successPrimaryHref?: string;
  /** Clave i18n del botón principal tras éxito (web) */
  successPrimaryLabelKey?: string;
  onDismissEmbedded?: () => void;
};

/**
 * Resultado de pago (ui-summary + polling). Misma lógica que la antigua PaymentResultPage.
 */
export function PaymentIntentResultPanel({
  paymentIntentId,
  tenantId,
  statusFromQuery,
  variant,
  moduleBasePath,
  successPrimaryHref,
  successPrimaryLabelKey,
  onDismissEmbedded,
}: PaymentIntentResultPanelProps) {
  const { t } = useTranslation();
  const [successCountdown, setSuccessCountdown] = useState<number | null>(null);
  const [summary, setSummary] = useState<PaymentIntentSummary | null>(null);
  const [resultStatus, setResultStatus] = useState<ResultStatus>('loading');
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successCountdownStartedRef = useRef(false);
  const { isReady, isTelegramWebApp, closeWebApp } = useTelegramWebApp();

  const successHref = successPrimaryHref ?? defaultBookingsUrl(tenantId);

  const effectiveModulePath = useMemo(() => {
    const fromSummary = summary?.moduleBasePath;
    if (fromSummary === '/booking' || fromSummary === '/restaurant') return fromSummary;
    return moduleBasePath;
  }, [summary?.moduleBasePath, moduleBasePath]);

  const homeHref = moduleHomeUrl(effectiveModulePath, tenantId);

  const standaloneMenuHref = useMemo(() => {
    const typ = String(summary?.type || '').toLowerCase();
    if (typ === 'gym_session_bundle' || typ === 'gym_bundle' || typ === 'gym_bono' || typ === 'gym_subscription' || typ === 'gym_plan') {
      return moduleHomeUrl('/gym', tenantId);
    }
    if (typ === 'booking') return moduleHomeUrl(effectiveModulePath, tenantId);
    return defaultBookingsUrl(tenantId);
  }, [summary?.type, effectiveModulePath, tenantId]);

  const manageBookingsHref = useMemo(() => {
    const q = new URLSearchParams();
    if (tenantId) q.set('tenant_id', tenantId);
    // El carrito no rellena bookingPreview (referenceId es el cart, no una reserva). Sin `email`,
    // /bookings no puede resolver identidad y sale vacío. Fallback al email de invitado guardado
    // durante la reserva (lo mismo que usa BookingsPage para identificar al usuario).
    const email =
      summary?.bookingPreview?.customerEmail?.trim() ||
      (tenantId ? getWebBookingGuestEmail(tenantId) : null) ||
      undefined;
    if (email) q.set('email', email);
    const qs = q.toString();
    return `${typeof window !== 'undefined' ? window.location.origin : ''}/bookings${qs ? `?${qs}` : ''}`;
  }, [tenantId, summary?.bookingPreview?.customerEmail]);

  useEffect(() => {
    if (!isReady || !paymentIntentId) return;

    const load = async () => {
      if (statusFromQuery === 'cancelled') {
        setSummary({
          status: 'cancelled',
          type: 'booking',
          referenceId: '',
          i18nKeys: {
            titleKey: 'payment.canceled.title',
            descriptionKey: 'payment.canceled.description',
          },
        });
        setResultStatus('cancelled');
        return;
      }

      try {
        const data = await PaymentService.getPaymentIntentUiSummary(
          paymentIntentId,
          tenantId || undefined,
        );
        setSummary(data);
        const statusNorm = (data.status || '').toLowerCase();
        if (statusNorm === 'succeeded') {
          setResultStatus('succeeded');
        } else if (statusNorm === 'failed') {
          setResultStatus('failed');
        } else if (statusNorm === 'cancelled' || statusNorm === 'canceled') {
          setResultStatus('cancelled');
        } else if (statusNorm === 'requires_capture') {
          setResultStatus('requires_capture');
        } else if (statusNorm === 'expired') {
          setResultStatus('expired');
        } else if (statusNorm === 'pending' || statusNorm === 'processing') {
          setResultStatus('pending');
        } else {
          setResultStatus('succeeded');
        }
      } catch (e: any) {
        if (e?.message?.includes('404') || e?.response?.status === 404) {
          setResultStatus('not_found');
          setSummary({
            status: 'not_found',
            type: '',
            referenceId: '',
            i18nKeys: {
              titleKey: 'payment.error.intent_not_found.title',
              descriptionKey: 'payment.error.intent_not_found.description',
            },
          });
        } else {
          setResultStatus('not_found');
          setSummary({
            status: 'not_found',
            type: '',
            referenceId: '',
            i18nKeys: {
              titleKey: 'payment.error.title',
              descriptionKey: 'payment.error.generic',
            },
          });
        }
      }
    };

    load();
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, [isReady, paymentIntentId, tenantId, statusFromQuery]);

  const isSuccess = resultStatus === 'succeeded' || resultStatus === 'requires_capture';
  useEffect(() => {
    if (!isSuccess) {
      successCountdownStartedRef.current = false;
      setSuccessCountdown(null);
      return;
    }
    if (!isTelegramWebApp) {
      setSuccessCountdown(null);
      return;
    }
    if (successCountdownStartedRef.current) return;
    successCountdownStartedRef.current = true;
    setSuccessCountdown(SUCCESS_AUTO_ACTION_SECONDS);
    const interval = setInterval(() => {
      setSuccessCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          if ((window as any).Telegram?.WebApp) {
            (window as any).Telegram.WebApp.close();
          } else if (closeWebApp) {
            closeWebApp();
          }
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isSuccess, isTelegramWebApp, closeWebApp, tenantId]);

  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (resultStatus !== 'pending' || !paymentIntentId) return;
    const effectiveTenantId = tenantId || undefined;
    let cancelled = false;
    const start = Date.now();

    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - start >= PENDING_TIMEOUT_MS) {
        setPollingTimedOut(true);
        return;
      }
      try {
        const data = await PaymentService.getPaymentIntentUiSummary(
          paymentIntentId,
          effectiveTenantId,
        );
        if (cancelled) return;
        const s = (data.status || '').toLowerCase();
        if (s === 'succeeded' || s === 'requires_capture' || s === 'failed' || s === 'cancelled' || s === 'canceled') {
          setSummary(data);
          setResultStatus(s === 'succeeded' || s === 'requires_capture' ? 'succeeded' : s === 'failed' ? 'failed' : 'cancelled');
          return;
        }
      } catch {
        // ignore
      }
      if (!cancelled) {
        pollTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL_MS);
      }
    };
    pollTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [resultStatus, paymentIntentId, tenantId]);

  useEffect(() => {
    if (!summary?.nextAction || resultStatus !== 'succeeded') return;
    if (summary.nextAction.type === 'close' && summary.nextAction.timeoutMs) {
      closeTimeoutRef.current = setTimeout(() => {
        if (isTelegramWebApp && (window as any).Telegram?.WebApp) {
          (window as any).Telegram.WebApp.close();
        } else if (closeWebApp) {
          closeWebApp();
        }
      }, summary.nextAction.timeoutMs);
    }
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [summary?.nextAction, resultStatus, isTelegramWebApp, closeWebApp]);

  const handleClose = () => {
    if (isTelegramWebApp && (window as any).Telegram?.WebApp) {
      (window as any).Telegram.WebApp.close();
    } else if (closeWebApp && isTelegramWebApp) {
      closeWebApp();
    } else if (variant === 'embedded' && onDismissEmbedded) {
      onDismissEmbedded();
    } else {
      window.location.replace(variant === 'embedded' ? homeHref : standaloneMenuHref);
    }
  };

  const handleRetry = () => {
    if (paymentIntentId) {
      window.location.href = tenantId ? `/pay/${paymentIntentId}?tenant_id=${tenantId}` : `/pay/${paymentIntentId}`;
    } else {
      handleClose();
    }
  };

  const containerClass =
    variant === 'embedded' ? 'payment-container payment-container--embedded' : 'payment-container';

  if (!paymentIntentId) {
    return (
      <TransactionError
        titleKey="payment.error.intent_not_found.title"
        descriptionKey="payment.error.intent_not_found.description"
        primaryAction={{ labelKey: 'payment.backToMenu', onClick: handleClose }}
      />
    );
  }

  if (resultStatus === 'loading' || (resultStatus === 'pending' && !summary)) {
    return <WebpaymentLoading state="validating" containerClassName={containerClass} />;
  }

  if (resultStatus === 'not_found') {
    return (
      <TransactionError
        titleKey={summary?.i18nKeys?.titleKey ?? 'payment.error.intent_not_found.title'}
        descriptionKey={summary?.i18nKeys?.descriptionKey ?? 'payment.error.intent_not_found.description'}
        primaryAction={{ labelKey: 'payment.backToMenu', onClick: handleClose }}
      />
    );
  }

  if (resultStatus === 'pending' && pollingTimedOut) {
    return (
      <div className={containerClass}>
        <div className="payment-content">
          <h2>{t('payment.confirming.title')}</h2>
          <p>{t('payment.confirming.description')}</p>
          <p className="payment-confirming-fallback">{t('success.errorConfirmTimeout')}</p>
          <button type="button" onClick={handleClose} className="close-button">
            {t('payment.close')}
          </button>
        </div>
      </div>
    );
  }

  const titleKey = summary?.i18nKeys?.titleKey ?? 'payment.success.title';
  let descriptionKey = summary?.i18nKeys?.descriptionKey ?? 'payment.success.description';
  if (!isTelegramWebApp && titleKey === 'payment.booking_confirmed.title') {
    if (descriptionKey === 'payment.booking_confirmed.description') descriptionKey = 'payment.booking_confirmed.description_web';
    else if (descriptionKey === 'payment.booking_confirmed.description_requires_capture') {
      descriptionKey = 'payment.booking_confirmed.description_requires_capture_web';
    }
  }
  const isErrorLike =
    resultStatus === 'failed' || resultStatus === 'cancelled' || resultStatus === 'canceled' || resultStatus === 'expired';

  return (
    <div className={containerClass}>
      <div className={`payment-content ${isErrorLike ? 'error' : ''}`}>
        {!isErrorLike && <div className="success-icon">✓</div>}
        {isErrorLike && <div className="error-icon">❌</div>}
        <h2>{t(titleKey)}</h2>
        <p>{t(descriptionKey)}</p>
        {isSuccess && summary?.bookingPreview && isReservationLikeType(String(summary.type)) ? (
          <div className="payment-booking-preview">
            <p className="payment-booking-preview-title">{t('payment.result.reservation_heading')}</p>
            {summary.bookingPreview.serviceName ? (
              <p className="payment-booking-preview-line">
                <strong>{summary.bookingPreview.serviceName}</strong>
              </p>
            ) : null}
            {summary.bookingPreview.slotStart ? (
              <p className="payment-booking-preview-line">
                {t('payment.result.when')}: {summary.bookingPreview.slotStart}
                {summary.bookingPreview.slotEnd ? ` — ${summary.bookingPreview.slotEnd}` : ''}
              </p>
            ) : null}
            <p className="payment-booking-preview-hint">{t('payment.result.email_hint')}</p>
            {summary.bookingPreview.slotStart && summary.bookingPreview.slotEnd ? (
              <div style={{ marginTop: 10 }}>
                <CalendarPickerButton
                  uid={summary.bookingPreview.bookingId ?? summary.referenceId ?? paymentIntentId}
                  serviceName={summary.bookingPreview.serviceName || 'Booking'}
                  slotStart={summary.bookingPreview.slotStart}
                  slotEnd={summary.bookingPreview.slotEnd}
                  filename={`reserva-${summary.referenceId || paymentIntentId}.ics`}
                  label={t('payment.result.add_to_calendar')}
                  labelGoogle="Google Calendar"
                  labelOther={t('payment.result.other_calendar')}
                  bookingId={summary.bookingPreview.bookingId}
                  tenantId={tenantId}
                />
              </div>
            ) : null}
          </div>
        ) : null}
        {isSuccess ? (
          <p
            className="payment-invoice-note"
            style={{ marginTop: 14, fontSize: '0.8rem', opacity: 0.7 }}
          >
            {t(
              'payment.result.invoice_nif_note',
              '¿Necesitas factura con NIF? Solicítala al negocio por su email o teléfono de contacto.',
            )}
          </p>
        ) : null}
        {isSuccess && isTelegramWebApp && successCountdown !== null && (
          <p
            className="payment-success-countdown"
            style={{ marginTop: 8, fontSize: '0.95rem', color: 'var(--text-secondary, #666)' }}
          >
            {t(successCountdown === 1 ? 'success.closeWebAppIn_one' : 'success.closeWebAppIn_other', {
              seconds: successCountdown,
            })}
          </p>
        )}
        {isSuccess && !isTelegramWebApp ? (
          <div className="payment-result-actions">
            <button
              type="button"
              onClick={() =>
                window.location.replace(
                  isReservationLikeType(String(summary?.type || ''))
                    ? manageBookingsHref
                    : successHref,
                )
              }
              className="close-button payment-result-actions-primary"
              style={{ marginTop: 12 }}
            >
              {t(successPrimaryLabelKey ?? primaryBtnKeyForType(String(summary?.type || '')))}
            </button>
          </div>
        ) : null}
        {variant === 'standalone' ? <WebPaymentTrustLinks /> : null}
        <div className="button-group">
          {(resultStatus === 'cancelled' || resultStatus === 'canceled' || resultStatus === 'expired' || resultStatus === 'failed') && (
            <button type="button" onClick={handleRetry} className="retry-button">
              {t('payment.retry')}
            </button>
          )}
          <button type="button" onClick={handleClose} className="close-button">
            {t('payment.backToMenu')}
          </button>
        </div>
      </div>
    </div>
  );
}
